import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { INotificationData } from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider, OrganizationSettings, updateOrganizationSettings } from '@modeso/aumico-lib-users-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Observable, filter, map, of } from 'rxjs';
import { DEFAULT_STYLE_OPTIONS } from '../../../../../../../aumico-lib-annualreport-fe/src/lib';
import { BasePageComponent } from '../../../../pages/base.page';

@UntilDestroy()
@Component({
    selector: 'app-page-organization-settings.page',
    templateUrl: './settings.page.html',
    styleUrls: ['./settings.page.scss'],
})
export class SettingsPageComponent extends BasePageComponent implements OnInit {
    private organizationSettings: OrganizationSettings | undefined;
    organizationSettingsToBeSaved: OrganizationSettings | undefined;
    private isSaveRequestInProgress: boolean;

    constructor(
        injector: Injector,
        private readonly organizationProvider: OrganizationProvider,
        dialog: MatDialog,
        protected override actionsListener$: ActionsSubject,
    ) {
        super(injector, dialog);
        this.organizationSettings = {
            titleColor: DEFAULT_STYLE_OPTIONS.titleColor,
            textColor: DEFAULT_STYLE_OPTIONS.textColor,
            backgroundColor: DEFAULT_STYLE_OPTIONS.backgroundColor,
        };
    }

    override ngOnInit(): void {
        this.organizationProvider.dispatchGetOrganizationSettings();
        this.organizationProvider
            .getOrganizationSettings()
            .pipe(
                filter((organizationSettings: OrganizationSettings) => organizationSettings != null),
                untilDestroyed(this),
            )
            .subscribe((organizationSettings: OrganizationSettings) => {
                this.organizationSettings = { ...this.organizationSettings, ...organizationSettings };
                this.organizationSettingsToBeSaved = cloneDeep(this.organizationSettings);
            });
        this.disableProceedButton = false;
        this.actionsListener$
            .pipe(ofType(updateOrganizationSettings.success), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());
        this.actionsListener$
            .pipe(ofType(updateOrganizationSettings.failure), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());
    }

    protected onSaveSuccessfully(): void {
        this.organizationSettings = this.organizationSettingsToBeSaved;
        this.organizationProvider.resetCurrentOrganization();
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(true);
        const notificationData: INotificationData = {
            titleKey: 'aumico_organization_settings_print_success_notification_title',
            descriptionKey: 'aumico_organization_settings_print_success_notification_description',
        };
        this.fireSuccessNotification(notificationData);
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    public onOrganizationSettingsChanged(organizationSettings: OrganizationSettings): void {
        this.organizationSettingsToBeSaved = cloneDeep(organizationSettings);
    }

    public saveButtonClicked = (): void => {
        this.save();
    };

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        }

        const isDataChangesSaved = fastDeepEqual(this.organizationSettings, this.organizationSettingsToBeSaved);
        return of(!isDataChangesSaved);
    }

    override save(): Observable<void> {
        this.isSaveRequestInProgress = true;

        this.organizationProvider.dispatchUpdateOrganizationSettings(this.organizationSettingsToBeSaved);
        return of(void 0);
    }
}
